import { Suspense, lazy } from 'react';
import { actions, registerActions } from '@/lib/actions';
import { api } from '@/lib/api';
import { openRecord } from '@/lib/overlays';
import { queryClient } from '@/lib/queryClient';
import { registerCommands, registerSlot, registerView, type SlotProps } from '@/lib/slots';
import { toastError } from '@/lib/toast';
import { Skeleton } from '@/ui/bits';
import type { DealCard, DealDetail } from '@shared/deals';
import type { ClientArea } from '../types';
import { keys } from './lib';

// The deals area: the pipeline board, the Deals box on records, and the dialogs to add, edit and close deals.
// Everything beyond this file loads on first use.

const DealsBox = lazy(() => import('./DealsBox'));

function DealsSlot(props: SlotProps['record.deals']) {
  return (
    <Suspense
      fallback={
        <section className="tile" aria-busy="true">
          <div className="tile-h">
            <h3>Deals</h3>
          </div>
          <Skeleton rows={2} />
        </section>
      }
    >
      <DealsBox {...props} />
    </Suspense>
  );
}

/** Runs a step that needs one of the area's dialogs, loading its code first. */
function withCode<T>(load: () => Promise<T>, run: (mod: T) => void): void {
  load().then(run, (err: unknown) => toastError(err));
}

async function openDeal(dealId: string): Promise<void> {
  try {
    const { cachedCard } = await import('./ops');
    let deal: Pick<DealCard, 'id' | 'person' | 'company'> | null = cachedCard(dealId);
    if (!deal) {
      const detail = await api.get<DealDetail>(`/deals/${dealId}`);
      queryClient.setQueryData(keys.detail(dealId), detail);
      deal = detail;
    }
    if (deal.person) openRecord('person', deal.person.id, { focus: dealId });
    else if (deal.company) openRecord('company', deal.company.id, { focus: dealId });
  } catch (err) {
    toastError(err);
  }
}

export const area: ClientArea = {
  name: 'deals',
  setup() {
    registerView('deals', () => import('./DealsView'));
    registerSlot('record.deals', DealsSlot);
    registerActions({
      closeDeal: (dealId, outcome) => withCode(() => import('./CloseDialog'), (m) => m.openCloseDialog(dealId, outcome)),
      newDeal: (init) => withCode(() => import('./DealDialog'), (m) => m.openDealDialog(init ?? {})),
      openDeal: (dealId) => void openDeal(dealId),
    });
    registerCommands(() => [
      {
        id: 'deals.new',
        title: 'New deal',
        sub: 'Add a deal to the pipeline',
        icon: 'plus',
        keywords: 'create add opportunity pipeline',
        run: () => actions.newDeal(),
        order: 115,
      },
      {
        id: 'deals.stages',
        title: 'Edit stages',
        sub: 'Rename, recolour and reorder the pipeline',
        icon: 'stage',
        keywords: 'pipeline stages columns stale',
        run: () => withCode(() => import('./StagesEditor'), (m) => m.openStagesEditor()),
        order: 125,
      },
    ]);
  },
};
